import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useUserPermission } from "@/hooks/use-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { getAiCreditsSummaryApi } from "@/services/api-service/ai-credits/ai-credits-api";
import { aiCreditsNavAlertState } from "@/store/ai-credits-nav";
import { getUserToken } from "@/services/storage-data";

let aiCreditsNavAlertFetchStarted = false;

function unwrapAvailableCredits(res: any): number | null {
  if (!res || typeof res !== "object") return null;
  if (typeof res.availableCredits === "number") return res.availableCredits;
  if (typeof res?.data?.availableCredits === "number") {
    return res.data.availableCredits;
  }
  if (typeof res?.data?.data?.availableCredits === "number") {
    return res.data.data.availableCredits;
  }
  return null;
}

/**
 * Loads AI Credits balance once (when the user can see AI Dashboard)
 * and toggles the sidebar red alert when balance is 0.
 */
export function useAiCreditsNavAlert() {
  const { callApi } = useGomakeAxios();
  const { CheckPermission } = useUserPermission();
  const [showAlert, setShowAlert] = useRecoilState(aiCreditsNavAlertState);

  useEffect(() => {
    if (aiCreditsNavAlertFetchStarted) return;
    if (!getUserToken()) return;
    if (!CheckPermission(Permissions.SHOW_AI_DASHBOARD)) return;

    aiCreditsNavAlertFetchStarted = true;
    let cancelled = false;

    void getAiCreditsSummaryApi(callApi, (res) => {
      if (cancelled) return;
      const available = unwrapAvailableCredits(res);
      const next = available != null ? available <= 0 : false;
      setShowAlert((prev) => (prev === next ? prev : next));
    }).catch(() => {
      aiCreditsNavAlertFetchStarted = false;
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callApi]);

  return showAlert;
}
